import { Question } from '../types/assessment';

export const lifestyleQuestions: Question[] = [
  // Sleep Assessment
  {
    id: 'sleep_quality',
    text: 'How would you rate your overall sleep quality?',
    type: 'multiple-choice',
    category: 'lifestyle',
    subcategory: 'sleep',
    required: true,
    options: [
      { value: 'excellent', label: 'Excellent', score: 4 },
      { value: 'good', label: 'Good', score: 3 },
      { value: 'fair', label: 'Fair', score: 2 },
      { value: 'poor', label: 'Poor', score: 1 },
      { value: 'very-poor', label: 'Very poor', score: 0 }
    ]
  },
  {
    id: 'sleep_hours',
    text: 'On average, how many hours of sleep do you get per night?',
    type: 'multiple-choice',
    category: 'lifestyle',
    subcategory: 'sleep',
    required: true,
    options: [
      { value: 'less-than-4', label: 'Less than 4 hours', score: 0 },
      { value: '4-5', label: '4-5 hours', score: 1 },
      { value: '6-7', label: '6-7 hours', score: 2 },
      { value: '7-8', label: '7-8 hours', score: 4 },
      { value: '8-9', label: '8-9 hours', score: 3 },
      { value: 'more-than-9', label: 'More than 9 hours', score: 2 }
    ]
  },
  {
    id: 'sleep_problems',
    text: 'Which sleep problems do you experience? (Select all that apply)',
    type: 'multi-select',
    category: 'lifestyle',
    subcategory: 'sleep',
    required: false,
    options: [
      { value: 'trouble-falling-asleep', label: 'Trouble falling asleep' },
      { value: 'frequent-waking', label: 'Waking up frequently during the night' },
      { value: 'early-waking', label: 'Waking up too early and unable to fall back asleep' },
      { value: 'restless-sleep', label: 'Restless or disturbed sleep' },
      { value: 'nightmares', label: 'Nightmares or disturbing dreams' },
      { value: 'snoring', label: 'Snoring or sleep apnea' },
      { value: 'racing-thoughts', label: 'Racing thoughts at bedtime' },
      { value: 'none', label: 'None of these' }
    ]
  },
  
  // Exercise and Physical Activity
  {
    id: 'exercise_frequency',
    text: 'How often do you engage in physical exercise or activity?',
    type: 'multiple-choice',
    category: 'lifestyle',
    subcategory: 'exercise',
    required: true,
    options: [
      { value: 'daily', label: 'Daily', score: 4 },
      { value: '4-6-times-week', label: '4-6 times per week', score: 3 },
      { value: '2-3-times-week', label: '2-3 times per week', score: 2 },
      { value: 'once-week', label: 'Once a week', score: 1 },
      { value: 'rarely', label: 'Rarely or never', score: 0 }
    ]
  },
  {
    id: 'exercise_duration',
    text: 'When you do exercise, how long is a typical session?',
    type: 'multiple-choice',
    category: 'lifestyle',
    subcategory: 'exercise',
    required: true,
    condition: {
      dependsOn: 'exercise_frequency',
      operator: 'not-equals',
      value: 'rarely'
    },
    options: [
      { value: 'less-than-15', label: 'Less than 15 minutes' },
      { value: '15-30', label: '15-30 minutes' },
      { value: '30-60', label: '30-60 minutes' },
      { value: 'more-than-60', label: 'More than 60 minutes' }
    ]
  },
  
  // Nutrition
  {
    id: 'eating_patterns',
    text: 'How would you describe your eating patterns?',
    type: 'multiple-choice',
    category: 'lifestyle',
    subcategory: 'nutrition',
    required: true,
    options: [
      { value: 'regular-healthy', label: 'Regular, balanced meals', score: 3 },
      { value: 'mostly-regular', label: 'Mostly regular, sometimes skip meals', score: 2 },
      { value: 'irregular', label: 'Irregular eating patterns', score: 1 },
      { value: 'poor', label: 'Poor eating habits, frequent skipping', score: 0 }
    ]
  },
  {
    id: 'appetite_changes',
    text: 'Have you noticed any changes in your appetite recently?',
    type: 'multiple-choice',
    category: 'lifestyle',
    subcategory: 'nutrition',
    required: true,
    options: [
      { value: 'no-change', label: 'No significant change' },
      { value: 'increased', label: 'Increased appetite' },
      { value: 'decreased', label: 'Decreased appetite' },
      { value: 'fluctuates', label: 'Appetite fluctuates significantly' }
    ]
  },
  
  // Daily Functioning
  {
    id: 'daily_functioning',
    text: 'How well are you able to handle your daily responsibilities?',
    type: 'multiple-choice',
    category: 'lifestyle',
    subcategory: 'functioning',
    required: true,
    options: [
      { value: 'excellent', label: 'Excellent - managing everything well', score: 4 },
      { value: 'good', label: 'Good - managing most things well', score: 3 },
      { value: 'fair', label: 'Fair - struggling with some things', score: 2 },
      { value: 'poor', label: 'Poor - struggling with many things', score: 1 },
      { value: 'very-poor', label: 'Very poor - unable to manage basic tasks', score: 0, triggerConditions: ['functioning_concern'] }
    ]
  },
  {
    id: 'work_performance',
    text: 'How has your work or academic performance been affected by your mental health?',
    type: 'multiple-choice',
    category: 'lifestyle',
    subcategory: 'functioning',
    required: true,
    options: [
      { value: 'not-affected', label: 'Not affected at all' },
      { value: 'slightly-affected', label: 'Slightly affected' },
      { value: 'moderately-affected', label: 'Moderately affected' },
      { value: 'significantly-affected', label: 'Significantly affected' },
      { value: 'unable-to-work', label: 'Unable to work/study due to mental health' },
      { value: 'not-applicable', label: 'Not applicable (not working/studying)' }
    ]
  },
  {
    id: 'self_care',
    text: 'How well are you taking care of your basic self-care needs?',
    type: 'multiple-choice',
    category: 'lifestyle',
    subcategory: 'functioning',
    required: true,
    options: [
      { value: 'excellent', label: 'Excellent - maintaining all self-care' },
      { value: 'good', label: 'Good - maintaining most self-care' },
      { value: 'fair', label: 'Fair - struggling with some self-care' },
      { value: 'poor', label: 'Poor - neglecting basic self-care' },
      { value: 'very-poor', label: 'Very poor - unable to maintain basic hygiene/care' }
    ]
  }
]; 